import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tab,
  Tabs,
  Tooltip,
  Typography
} from '@material-ui/core';
import CloseIcon from '@mui/icons-material/CloseRounded';
import DownloadIcon from '@mui/icons-material/FileDownloadOutlined';
import DeleteIcon from '@mui/icons-material/DeleteOutlineRounded';
import moment from 'moment';
import toast from 'react-hot-toast';
import { questionnaireApi } from '../../../../api/questionnaireApi';
import Scrollbar from '../../../Scrollbar';
import ScrollJumpButtons from '../../../ScrollJumpButtons';
import AnswerBar from './AnswerBar';
import { ScoreRing } from './SessionResponseCard';
import StarRating from './StarRating';
import { alpha } from '@mui/material/styles';
import { grey, greyAlpha } from '../../../../theme/tones';
import {
  answerValues,
  formatDateRange,
  formatStyle,
  getTypeConfig,
  isIdentityQuestion,
  renderAnswer,
  scoreColor
} from './constants';

const PER_PAGE = 100;

/** Hauteur de la Toolbar de DashboardNavbar (minHeight: 64). */
const NAVBAR_HEIGHT = 64;

/**
 * La colonne « Apprenant » reste figée pendant le défilement horizontal : avec
 * une vingtaine de colonnes de questions, on perd sinon de vue à qui appartient
 * la ligne qu'on est en train de lire.
 */
const stickyLearnerCellSx = (background, zIndex) => ({
  position: 'sticky',
  left: 0,
  zIndex,
  bgcolor: background,
  minWidth: 230,
  // Dégradé débordant à droite : matérialise la césure avec les colonnes
  // qui défilent sous la colonne figée.
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    width: 8,
    transform: 'translateX(100%)',
    pointerEvents: 'none',
    background: `linear-gradient(90deg, ${greyAlpha(900, 0.08)}, ${greyAlpha(900, 0)})`
  }
});

const parseAnswers = (raw) => {
  if (!raw) return {};
  if (typeof raw === 'object') return raw;
  try {
    return JSON.parse(raw);
  } catch (e) {
    return {};
  }
};

/**
 * Tiroir de détail d'une session : la table brute des réponses (colonne par
 * question) et une synthèse question par question calculée côté client à partir
 * des mêmes lignes — aucun appel supplémentaire.
 */
/**
 * Largeur d'une colonne de question, déduite de sa réponse la plus longue.
 *
 * Les réponses s'affichent en entier : enroulée dans la largeur d'origine, une
 * question ouverte étirait la ligne sur une dizaine de lignes de texte et
 * déformait tout le tableau. Seule la colonne concernée s'élargit, les
 * questions à choix gardent la leur.
 */
/**
 * Largeurs des colonnes qui ne portent pas de réponse. Elles sont explicites
 * parce que le tableau passe en `table-layout: fixed` : c'est la seule façon
 * d'obtenir qu'une colonne de remarques garde vraiment la largeur qu'on lui
 * donne — en `auto`, le navigateur la recomprime au profit des autres.
 */
const FIXED_COLUMNS = { learner: 260, profession: 170, date: 140, score: 90, actions: 60 };

const answerColumnWidth = (longest) => {
  if (longest <= 60) return 280;
  if (longest <= 160) return 400;
  if (longest <= 400) return 560;
  return 680;
};

const SessionResponsesDrawer = ({ group, type, accent, onClose, onDeleted, onDownloadReport, reportLoading }) => {
  const [tab, setTab] = useState('reponses');
  const [rows, setRows] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // `containerRef` de PerfectScrollbar : c'est ce nœud qui porte le scrollTop,
  // pas le composant Scrollbar lui-même.
  const tableScrollRef = useRef(null);
  const synthesisScrollRef = useRef(null);
  const getTableScroller = useCallback(() => tableScrollRef.current, []);
  const getSynthesisScroller = useCallback(() => synthesisScrollRef.current, []);

  const load = useCallback(async (signal) => {
    if (!group) return;
    try {
      setLoading(true);
      setError(null);
      const accessToken = window.localStorage.getItem('accessToken');
      const res = await questionnaireApi.fetchSubmissionsDetail(
        type,
        { formation_id: group.formation_id, session_id: group.session_id },
        accessToken,
        1,
        PER_PAGE,
        signal
      );
      if (res && res.status) {
        setRows(res.data || []);
        setQuestions(res.questions || []);
      } else {
        setError(res?.message || 'Chargement impossible.');
      }
    } catch (err) {
      if (err.name !== 'AbortError') setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [group, type]);

  useEffect(() => {
    if (!group) return undefined;
    const controller = new AbortController();
    setTab('reponses');
    load(controller.signal);
    return () => controller.abort();
  }, [group, load]);

  /**
   * Rang d'une question parmi celles qui portent le MÊME intitulé dans son
   * questionnaire. Le gabarit de satisfaction répète « Remarques » après chaque
   * bloc (accès, interactivité, formateur, documents) : sans ce rang, les 2 à 4
   * questions se confondaient en une seule colonne, qui n'affichait que la
   * dernière et masquait les réponses des autres.
   */
  const questionRanks = useMemo(() => {
    const seen = new Map();
    const ranks = new Map();
    questions.forEach((q) => {
      const key = `${q.questionnaire_id}::${q.text}`;
      const rank = (seen.get(key) || 0) + 1;
      seen.set(key, rank);
      ranks.set(q.id, rank);
    });
    return ranks;
  }, [questions]);

  /** Intitulé → nombre d'occurrences dans le questionnaire qui en porte le plus. */
  const maxRankByText = useMemo(() => {
    const map = new Map();
    questions.forEach((q) => {
      const rank = questionRanks.get(q.id) || 1;
      if (rank > (map.get(q.text) || 0)) map.set(q.text, rank);
    });
    return map;
  }, [questions, questionRanks]);

  /** Une colonne par (intitulé, rang), dédoublonnée entre questionnaires. */
  const columns = useMemo(() => {
    const seen = new Set();
    const out = [];
    questions.forEach((q) => {
      if (isIdentityQuestion(q.text)) return;
      const rank = questionRanks.get(q.id) || 1;
      const key = `${q.text}::${rank}`;
      if (seen.has(key)) return;
      seen.add(key);
      // Intitulé répété : on le numérote pour que l'entête reste distinguable.
      const label = (maxRankByText.get(q.text) || 1) > 1 ? `${q.text} ${rank}` : q.text;
      out.push({ ...q, rank, label });
    });
    return out;
  }, [questions, questionRanks, maxRankByText]);

  const answersByRow = useMemo(
    () => rows.map((r) => parseAnswers(r.answers)),
    [rows]
  );

  /** (questionnaire, intitulé, rang) → id de question, pour retrouver la bonne réponse. */
  const questionIdsByKey = useMemo(() => {
    const map = new Map();
    questions.forEach((q) => {
      const key = `${q.questionnaire_id}::${q.text}::${questionRanks.get(q.id) || 1}`;
      map.set(key, q.id);
    });
    return map;
  }, [questions, questionRanks]);

  /** Colonne → largeur, d'après la réponse la plus longue qu'elle porte. */
  const columnWidths = useMemo(() => {
    const map = new Map();
    columns.forEach((col) => {
      let longest = 0;
      rows.forEach((row, i) => {
        const qid = questionIdsByKey.get(`${row.questionnaire_id}::${col.text}::${col.rank}`);
        if (!qid) return;
        const text = renderAnswer(answersByRow[i][qid]);
        if (text && text.length > longest) longest = text.length;
      });
      map.set(col.id, answerColumnWidth(longest));
    });
    return map;
  }, [columns, rows, answersByRow, questionIdsByKey]);

  /**
   * Largeur totale à réserver au tableau. En `table-layout: auto`, une largeur
   * posée sur la cellule n'est qu'une indication : si la somme dépasse le
   * conteneur, le navigateur recomprime les colonnes — c'est ce qui ramenait
   * les colonnes de remarques à leur ancienne étroitesse. On force donc le
   * conteneur à la somme voulue et on laisse le défilement horizontal jouer.
   */
  const tableMinWidth = useMemo(() => {
    const fixed = Object.values(FIXED_COLUMNS).reduce((sum, w) => sum + w, 0);
    const questionsWidth = columns.reduce((sum, c) => sum + (columnWidths.get(c.id) || 280), 0);
    return Math.max(900, fixed + questionsWidth);
  }, [columns, columnWidths]);

  const synthesis = useMemo(() => columns.map((col) => {
    const counts = new Map();
    let answered = 0;
    let multi = false;

    rows.forEach((row, i) => {
      const qid = questionIdsByKey.get(`${row.questionnaire_id}::${col.text}::${col.rank}`);
      if (!qid) return;
      // Chaque case cochée compte pour elle-même : sur une question à choix
      // multiples, le total des barres dépasse donc le nombre de répondants.
      const values = answerValues(answersByRow[i][qid]);
      if (!values.length) return;
      answered += 1;
      if (values.length > 1) multi = true;
      values.forEach((v) => counts.set(v, (counts.get(v) || 0) + 1));
    });

    const entries = [...counts.entries()].sort((a, b) => b[1] - a[1]);
    // Une question ouverte génère presque autant de réponses distinctes que de
    // répondants : on l'affiche en verbatims plutôt qu'en barres.
    const isOpen = !multi && answered > 2 && entries.length > answered * 0.7;
    return { question: col, entries, answered, isOpen, multi };
  }), [columns, rows, answersByRow, questionIdsByKey]);

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer définitivement cette réponse ?')) return;
    try {
      const accessToken = window.localStorage.getItem('accessToken');
      const res = await questionnaireApi.deleteSubmission(id, accessToken);
      if (res.status) {
        toast.success('Réponse supprimée');
        setRows((prev) => prev.filter((r) => r.id !== id));
        if (onDeleted) onDeleted();
      } else {
        toast.error('Suppression impossible');
      }
    } catch (err) {
      toast.error('Erreur technique');
    }
  };

  const fmt = group ? formatStyle(group.format) : null;

  return (
    <Drawer
      anchor="right"
      open={Boolean(group)}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', md: '92vw' },
          maxWidth: 1400,
          bgcolor: grey(50),
          backgroundImage: 'none',
          // La barre de navigation du dashboard a un zIndex supérieur à celui
          // du Drawer : sans ce décalage elle recouvre l'en-tête du tiroir.
          top: NAVBAR_HEIGHT,
          height: `calc(100% - ${NAVBAR_HEIGHT}px)`
        }
      }}
    >
      {group && (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          {/* En-tête */}
          <Box sx={{ p: 3, pb: 0, bgcolor: 'background.paper', borderBottom: `1px solid ${grey(200)}` }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.6, flexShrink: 0 }}>
                <ScoreRing value={group.average} size={72} />
                {getTypeConfig(type).rating && (
                  <StarRating percent={group.average} size="0.78rem" color={scoreColor(group.average)} iconSize="0.95rem" />
                )}
              </Box>
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 1 }}>
                  <Chip
                    label={type}
                    size="small"
                    sx={{ bgcolor: `${accent}1F`, color: accent, fontWeight: 800, fontSize: '0.65rem', height: 21 }}
                  />
                  <Chip
                    label={group.format || 'Format inconnu'}
                    size="small"
                    sx={{ bgcolor: fmt.bg, color: fmt.color, fontWeight: 800, fontSize: '0.65rem', height: 21 }}
                  />
                  <Chip
                    label={`Session ${group.session_number ?? '—'}`}
                    size="small"
                    sx={{ bgcolor: (theme) => alpha(theme.palette.text.secondary, 0.1), color: 'text.secondary', fontWeight: 800, fontSize: '0.65rem', height: 21 }}
                  />
                </Box>
                <Typography sx={{ fontWeight: 800, fontSize: '1.15rem', color: 'text.primary', lineHeight: 1.3 }}>
                  {group.formation_name || 'Formation inconnue'}
                </Typography>
                <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary', fontWeight: 600, mt: 0.5 }}>
                  {formatDateRange(group.start_date, group.end_date)}
                  {(group.meeting_town || group.meeting_place_name) && ` · ${group.meeting_town || group.meeting_place_name}`}
                  {` · ${group.responses} réponse${group.responses > 1 ? 's' : ''}`}
                  {` · ${group.learners} apprenant${group.learners > 1 ? 's' : ''}`}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {onDownloadReport && (
                  <Button
                    onClick={() => onDownloadReport(group)}
                    disabled={reportLoading}
                    variant="outlined"
                    startIcon={reportLoading ? <CircularProgress size={15} color="inherit" /> : <DownloadIcon sx={{ fontSize: 18 }} />}
                    sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 800, borderColor: 'divider', color: 'text.primary' }}
                  >
                    {/* « de session » n'est pas décoratif : le document couvre
                        Pré, Post et Satisfaction, pas le seul onglet ouvert. */}
                    Compte rendu de session
                  </Button>
                )}
                <IconButton onClick={onClose} sx={{ color: 'text.secondary' }}>
                  <CloseIcon />
                </IconButton>
              </Box>
            </Box>

            <Tabs
              value={tab}
              onChange={(e, v) => setTab(v)}
              TabIndicatorProps={{ sx: { bgcolor: accent, height: 3, borderRadius: '3px 3px 0 0' } }}
              sx={{ mt: 2, minHeight: 42 }}
            >
              <Tab
                value="reponses"
                label={`Réponses (${rows.length})`}
                sx={{ minHeight: 42, textTransform: 'none', fontWeight: 800, fontSize: '0.8rem', '&.Mui-selected': { color: accent } }}
              />
              <Tab
                value="synthese"
                label={`Synthèse par question (${columns.length})`}
                sx={{ minHeight: 42, textTransform: 'none', fontWeight: 800, fontSize: '0.8rem', '&.Mui-selected': { color: accent } }}
              />
            </Tabs>
          </Box>

          {/* Contenu */}
          <Box sx={{ flexGrow: 1, overflow: 'hidden', p: 2.5 }}>
            {loading && (
              <Box sx={{ display: 'grid', placeItems: 'center', height: '100%' }}>
                <CircularProgress sx={{ color: accent }} />
              </Box>
            )}

            {!loading && error && (
              <Box sx={{ display: 'grid', placeItems: 'center', height: '100%' }}>
                <Typography sx={{ color: 'error.main', fontWeight: 700 }}>{error}</Typography>
              </Box>
            )}

            {!loading && !error && tab === 'reponses' && (
              <Box sx={{ position: 'relative', bgcolor: 'background.paper', borderRadius: '18px', border: `1px solid ${grey(200)}`, height: '100%', overflow: 'hidden' }}>
                <ScrollJumpButtons
                  getTarget={getTableScroller}
                  watch={`reponses-${rows.length}`}
                  accent={accent}
                  offset={14}
                />
                <Scrollbar containerRef={(el) => { tableScrollRef.current = el; }}>
                  <Box sx={{ minWidth: tableMinWidth }}>
                    <Table size="small" stickyHeader sx={{ tableLayout: 'fixed' }}>
                      <TableHead>
                        <TableRow>
                          <TableCell
                            sx={{
                              fontWeight: 800, color: 'text.secondary', fontSize: '0.68rem',
                              textTransform: 'uppercase', whiteSpace: 'nowrap',
                              width: FIXED_COLUMNS.learner,
                              // zIndex 3 : au-dessus des autres cellules d'en-tête
                              // (que stickyHeader place déjà en zIndex 2).
                              ...stickyLearnerCellSx(grey(50), 3)
                            }}
                          >
                            Apprenant
                          </TableCell>
                          {[
                            { label: 'Profession', width: FIXED_COLUMNS.profession },
                            { label: 'Date', width: FIXED_COLUMNS.date },
                            { label: 'Score', width: FIXED_COLUMNS.score }
                          ].map((h) => (
                            <TableCell
                              key={h.label}
                              sx={{ fontWeight: 800, color: 'text.secondary', fontSize: '0.68rem', textTransform: 'uppercase', bgcolor: 'action.hover', whiteSpace: 'nowrap', width: h.width }}
                            >
                              {h.label}
                            </TableCell>
                          ))}
                          {columns.map((c) => (
                            <TableCell
                              key={c.id}
                              sx={{
                                fontWeight: 800,
                                color: 'text.secondary',
                                fontSize: '0.68rem',
                                bgcolor: 'action.hover',
                                minWidth: 200,
                                width: columnWidths.get(c.id) || 280,
                                maxWidth: columnWidths.get(c.id) || 280
                              }}
                            >
                              <Tooltip title={c.label || c.text}>
                                <Box
                                  sx={{
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden'
                                  }}
                                >
                                  {c.label || c.text}
                                </Box>
                              </Tooltip>
                            </TableCell>
                          ))}
                          <TableCell sx={{ bgcolor: 'action.hover', width: FIXED_COLUMNS.actions }} />
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {rows.map((row, i) => {
                          const ratio = row.total_points > 0 ? (row.score / row.total_points) * 100 : null;
                          const color = scoreColor(ratio);
                          const date = moment(row.created_at);
                          return (
                            <TableRow
                              key={row.id}
                              sx={{
                                '& > td': { verticalAlign: 'top' },
                                // Le survol est repeint cellule par cellule : la
                                // colonne figée est opaque, la teinte posée sur
                                // la ligne ne la traverserait pas.
                                '&:hover > td': { bgcolor: 'action.hover' }
                              }}
                            >
                              {/* En `table-layout: fixed`, un `nowrap` déborderait
                                  sur la colonne voisine : on enroule. */}
                              <TableCell sx={{ wordBreak: 'break-word', ...stickyLearnerCellSx(grey(0), 1) }}>
                                <Typography sx={{ fontWeight: 800, fontSize: '0.82rem', color: 'text.primary' }}>
                                  {row.lastname} {row.firstname}
                                </Typography>
                                <Typography sx={{ fontSize: '0.68rem', color: 'text.disabled', fontWeight: 600 }}>
                                  {row.email || '—'}
                                </Typography>
                              </TableCell>
                              <TableCell sx={{ fontSize: '0.78rem', color: 'text.secondary', wordBreak: 'break-word' }}>
                                {row.profession || '—'}
                              </TableCell>
                              <TableCell sx={{ fontSize: '0.76rem', color: 'text.secondary', fontWeight: 600, whiteSpace: 'nowrap' }}>
                                {date.isValid() ? date.format('DD/MM/YYYY HH:mm') : '—'}
                              </TableCell>
                              <TableCell>
                                <Box
                                  sx={{
                                    px: 1.2, py: 0.3, borderRadius: '8px', display: 'inline-flex',
                                    bgcolor: `${color}1A`, color, fontWeight: 900, fontSize: '0.72rem'
                                  }}
                                >
                                  {ratio === null ? '—' : `${ratio.toFixed(1)}%`}
                                </Box>
                              </TableCell>
                              {columns.map((c) => {
                                const qid = questionIdsByKey.get(`${row.questionnaire_id}::${c.text}::${c.rank}`);
                                const text = qid ? renderAnswer(answersByRow[i][qid]) : '—';
                                return (
                                  <TableCell
                                    key={c.id}
                                    sx={{ maxWidth: columnWidths.get(c.id) || 280, verticalAlign: 'top' }}
                                  >
                                    {/* Réponse affichée en entier : les
                                        commentaires libres sont le fond de la
                                        satisfaction, les tronquer les rendait
                                        illisibles hors survol. `pre-wrap`
                                        conserve les retours à la ligne saisis. */}
                                    <Typography
                                      sx={{
                                        fontSize: '0.78rem',
                                        color: 'text.secondary',
                                        whiteSpace: 'pre-wrap',
                                        wordBreak: 'break-word'
                                      }}
                                    >
                                      {text}
                                    </Typography>
                                  </TableCell>
                                );
                              })}
                              <TableCell align="right">
                                <Tooltip title="Supprimer">
                                  <IconButton size="small" onClick={() => handleDelete(row.id)} sx={{ color: 'error.main' }}>
                                    <DeleteIcon sx={{ fontSize: 18 }} />
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                    {rows.length === 0 && (
                      <Box sx={{ py: 8, textAlign: 'center' }}>
                        <Typography sx={{ color: 'text.disabled', fontWeight: 700 }}>Aucune réponse.</Typography>
                      </Box>
                    )}
                  </Box>
                </Scrollbar>
              </Box>
            )}

            {!loading && !error && tab === 'synthese' && (
              <Box sx={{ position: 'relative', height: '100%', overflow: 'hidden' }}>
                <ScrollJumpButtons
                  getTarget={getSynthesisScroller}
                  watch={`synthese-${synthesis.length}`}
                  accent={accent}
                  offset={14}
                />
                <Scrollbar containerRef={(el) => { synthesisScrollRef.current = el; }}>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, minmax(0, 1fr))' },
                      gap: 2,
                      pr: 1,
                      pb: 2
                    }}
                  >
                    {synthesis.map(({ question, entries, answered, isOpen, multi }) => (
                      <Box
                        key={question.id}
                        sx={{ p: 2.25, bgcolor: 'background.paper', borderRadius: '18px', border: `1px solid ${grey(200)}` }}
                      >
                        <Typography sx={{ fontWeight: 800, fontSize: '0.85rem', color: 'text.primary', mb: 0.5 }}>
                          {question.label || question.text}
                        </Typography>
                        <Typography sx={{ fontSize: '0.7rem', color: 'text.disabled', fontWeight: 700, mb: 1.5 }}>
                          {answered} répondant{answered > 1 ? 's' : ''}
                          {isOpen && ' · réponses libres'}
                          {multi && ' · choix multiples'}
                        </Typography>
                        <Divider sx={{ mb: 1.5, borderColor: 'divider' }} />

                        {entries.length === 0 && (
                          <Typography sx={{ fontSize: '0.78rem', color: 'text.disabled' }}>Aucune réponse.</Typography>
                        )}

                        {!isOpen && entries.slice(0, 8).map(([label, count]) => (
                          <AnswerBar key={label} label={label} count={count} total={answered} accent={accent} />
                        ))}

                        {isOpen && entries.slice(0, 6).map(([label]) => (
                          <Typography
                            key={label}
                            sx={{
                              fontSize: '0.78rem', color: 'text.primary', mb: 0.75, pl: 1.5,
                              borderLeft: `3px solid ${accent}40`, fontStyle: 'italic'
                            }}
                          >
                            « {label} »
                          </Typography>
                        ))}

                        {entries.length > (isOpen ? 6 : 8) && (
                          <Typography sx={{ fontSize: '0.7rem', color: 'text.disabled', fontWeight: 700, mt: 0.5 }}>
                            +{entries.length - (isOpen ? 6 : 8)} autre{entries.length - (isOpen ? 6 : 8) > 1 ? 's' : ''}
                          </Typography>
                        )}
                      </Box>
                    ))}
                  </Box>
                </Scrollbar>
              </Box>
            )}
          </Box>
        </Box>
      )}
    </Drawer>
  );
};

SessionResponsesDrawer.propTypes = {
  group: PropTypes.object,
  type: PropTypes.string.isRequired,
  accent: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  onDeleted: PropTypes.func,
  onDownloadReport: PropTypes.func,
  reportLoading: PropTypes.bool
};

export default SessionResponsesDrawer;
